/* eslint-disable react/prop-types */
import React from 'react'
import T from 'prop-types'
import BlockContent from '@sanity/block-content-to-react'
import useSWRImmutable from 'swr/immutable'

import BEMHelper from '../../helpers/bem'
import styles from './Styles.module.scss'
import { ListItem, List } from '../List'
import Image from '../Image'
import { byline, hasLongWord } from '../../helpers/utils'
import docToUrl from '../../helpers/docToUrl'
import { dataset, projectId } from '../../api/sanity'
import Link from '../Link'
import InlineReference from '../InlineReference'

const BlockRenderer = (props) => {
  const { style = 'normal' } = props.node

  if (style === 'normal') {
    return <p {...bem('text')}>{props.children}</p>
  }

  if (style === 'h2') {
    return (
      <>
        <span {...bem('separator')} />
        <h2 {...bem('sub-title')}>{props.children}</h2>
      </>
    )
  }

  if (style === 'preamble') {
    return <p {...bem('preamble')}>{props.children}</p>
  }

  // Fall back to default handling
  return BlockContent.defaultSerializers.types.block(props)
}

function Factbox({ node: { title, content } }) {
  return (
    <aside {...bem('factbox')}>
      <h3>{title}</h3>
      <p>{content}</p>
    </aside>
  )
}

function RenderImage({ node }) {
  return <Image {...node} {...bem('image', node.type)} alt={''} />
}

function Gallery({ node }) {
  const images = node?.images || []
  const caption = node?.caption

  return (
    <figure {...bem('gallery')}>
      <div {...bem('images')}>
        {images.map(({ _key, ...image }) => (
          <Image key={_key} {...image} alt={''} />
        ))}
      </div>
      {caption && <figcaption {...bem('caption')}>{caption}</figcaption>}
    </figure>
  )
}

const fetcher = (...args) => fetch(...args).then((res) => res.json())

function MicrofactsMark(props) {
  const id = props.mark?.collectionId
  const lang = props.mark?.lang
  const { data, error } = useSWRImmutable(
    id && lang ? `/api/factiverse/microfact-by-id?id=${id}&lang=${lang}` : null,
    fetcher,
    {},
  )
  let content = ''
  let sourceUrl = ''
  if (!data && !error) {
    content = 'Laster…'
  } else if (data) {
    const spot = data.spots?.find((spot) => spot.entity._id === props.mark.entityId)
    const kbRef = spot?.entity?.kbs_refs?.find((kbRef) => kbRef.kb_id === props.mark.kb_id)
    if (kbRef) {
      content = kbRef.description
      sourceUrl = kbRef.pageUrl
    } else {
      content = ''
    }
  }
  return (
    <InlineReference
      mark={{
        content: content,
      }}
      sourceUrl={sourceUrl}
    >
      {props.children}
    </InlineReference>
  )
}

const serializers = {
  list({ type, ...props }) {
    return <List type={type === 'bullet' ? 'ul' : 'ol'} {...props} {...bem('list')} />
  },
  listItem(props) {
    return <ListItem {...props} />
  },
  types: {
    block: BlockRenderer,
    factbox: Factbox,
    image: RenderImage,
    gallery: Gallery,
  },
  marks: {
    internalLink(props) {
      const url = docToUrl(props?.mark?.target)
      return <Link href={url}>{props.children}</Link>
    },
    link(props) {
      return <a href={props.mark.href}>{props.children}</a>
    },
    definition(props) {
      return <InlineReference {...props} />
    },
    microfacts: MicrofactsMark,
  },
}

const bem = new BEMHelper(styles)

export default function Article({ title, preamble, image, text, type, children, ...props }) {
  const updated = byline(props)
  const portrait = image?.dimensions?.aspectRatio < 1
  const smallTitle = hasLongWord(title, 15)

  const isArticle = type !== 'page'

  return (
    <article {...bem('')}>
      <header {...bem('header', { image, portrait })}>
        <div {...bem('header-content')}>
          <h1 {...bem('title', { small: portrait && smallTitle })}>{title}</h1>
          {isArticle && <span {...bem('byline')}>{updated}</span>}
        </div>
        {image && <Image {...bem('main-image')} {...image} alt={''} />}
      </header>

      {preamble && <p {...bem('preamble')}>{preamble}</p>}

      {text && (
        <BlockContent
          renderContainerOnSingleChild
          {...bem('content')}
          blocks={text}
          serializers={serializers}
          dataset={dataset}
          projectId={projectId}
        />
      )}

      {children}
    </article>
  )
}

Article.propTypes = {
  title: T.string.isRequired,
  preamble: T.string,
  type: T.string,
  image: T.object,
  text: T.array,
  children: T.any,
}
